import {
  Box,
  Grid,
  GridItem,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetCreditFacilityQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import {
  getClassToCapitalProviderMap,
  parseInterestRateDisplayForCapitalProvider,
  sumTotalCommitmentAmount,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/utils';
import { FiArrowRight } from 'react-icons/fi';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

const creditAgreementsOverviewTableHeaderLabels: string[] = [
  'Borrower',
  'Lenders',
  'Facility type',
  'Commitment amount',
  'Interest rate',
  'Maturity date',
  '',
];

const CreditAgreementsOverviewTableHeader = () => {
  return (
    <Thead>
      <Tr>
        {_.map(creditAgreementsOverviewTableHeaderLabels, (header) => (
          <Th key={`header-${header}`}>{header}</Th>
        ))}
      </Tr>
    </Thead>
  );
};

type CreditAgreementsOverviewTableSkeletonRowProps = { width: string };
const CreditAgreementsOverviewTableSkeletonRow = ({
  width,
}: CreditAgreementsOverviewTableSkeletonRowProps) => {
  return (
    <Tr>
      {_.map(creditAgreementsOverviewTableHeaderLabels, (header) => (
        <Td key={`skeleton-${header}`}>
          <Skeleton height="25px" w={width} />
        </Td>
      ))}
    </Tr>
  );
};

type CreditAgreementInterestRateDisplayProps = {
  capitalProviders: CreditAgreement.DomainEntity.Items.CapitalProviderEntity[];
  classMechanics?: CreditAgreement.DomainEntity.ClassMechanicsWithEntities;
};
const CreditAgreementInterestRateDisplay = ({
  capitalProviders,
  classMechanics,
}: CreditAgreementInterestRateDisplayProps) => {
  // if facility is not classed, render interest rates labeled by capital provider
  if (!classMechanics) {
    return (
      <Box>
        {_.map(capitalProviders, (capitalProvider) => {
          return (
            <Text key={`capitalProvider-${capitalProvider.entity.slug}`}>
              {`${commonUtils.toTitleCase(
                capitalProvider.entity.slug,
              )}: ${parseInterestRateDisplayForCapitalProvider(
                capitalProvider,
              )}`}
            </Text>
          );
        })}
      </Box>
    );
  }

  const classToCapitalProviderMap = getClassToCapitalProviderMap({
    capitalProviders,
    classMechanics,
  });

  // if facility is classed, render interest rates labeled by class
  return (
    <Box>
      {_.map(classMechanics.classes, (classMechanic) => {
        const classLabel = classMechanic.classLabel.value;
        const capitalProviderForClass = classToCapitalProviderMap[classLabel];

        finleyAssert(
          capitalProviderForClass,
          'at least one capital provider party entity must exist for each existing class',
        );
        return (
          <Text>
            {`Class ${_.upperCase(
              classLabel,
            )}: ${parseInterestRateDisplayForCapitalProvider(
              capitalProviderForClass,
            )}`}
          </Text>
        );
      })}
    </Box>
  );
};

type CreditAgreementOverviewTableRowProps = {
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
};
const CreditAgreementOverviewTableRow = ({
  creditAgreement,
}: CreditAgreementOverviewTableRowProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { data, loading } = useGetCreditFacilityQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { creditFacilityId: creditAgreement.creditFacilityId },
  });

  if (!data || !data.creditFacility || loading) {
    return <CreditAgreementsOverviewTableSkeletonRow width="100px" />;
  }

  const {
    id,
    facilityMechanics: {
      parties: { borrowers, capitalProviders },
      finalMaturityDate,
    },
    classMechanics,
  } = creditAgreement;

  const { type: facilityType } = data.creditFacility;

  return (
    <Tr
      onClick={() => {
        navigate(`/credit-agreements/${creditAgreement.id}`);
      }}
      _hover={{ backgroundColor: 'gray.50', cursor: 'pointer' }}
    >
      <Td>{borrowers[0].borrowerEntityName.value}</Td>
      <Td>
        {_.map(capitalProviders, (capitalProvider) => (
          <Text
            key={`capital-provider-list-item-${id}-${capitalProvider.entity.id}`}
          >
            {capitalProvider.entity.name}
          </Text>
        ))}
      </Td>
      <Td>{commonUtils.toSentenceCase(facilityType)}</Td>
      <Td>
        <Grid templateColumns="repeat(5, 1fr)">
          <GridItem colSpan={1} textAlign="left">
            {/* In the future, get currency with credit agreement universal currency */}
            {commonUtils.getCurrencySymbol(Bank.Currency.USD)}
          </GridItem>
          <GridItem colSpan={4} textAlign="right">
            {commonUtils.numberWithCommasAndDecimals(
              sumTotalCommitmentAmount(capitalProviders),
            )}
          </GridItem>
        </Grid>
      </Td>
      <Td>
        <CreditAgreementInterestRateDisplay
          classMechanics={classMechanics}
          capitalProviders={capitalProviders}
        />
      </Td>
      <Td>{finalMaturityDate.value}</Td>
      <Td>
        <FiArrowRight />
      </Td>
    </Tr>
  );
};

type CreditAgreementsOverviewTableProps = {
  organizationId: string;
};
export const CreditAgreementsOverviewTable = ({
  organizationId,
}: CreditAgreementsOverviewTableProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();

  const {
    loading: creditAgreementArtifactsLoading,
    data: creditAgreementArtifactsData,
  } = useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery({
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error loading current credit agreements for organization.`;
        toast(errorToastConfig);
      }
    },
    fetchPolicy: 'network-only',
  });

  if (
    creditAgreementArtifactsLoading ||
    !creditAgreementArtifactsData ||
    !creditAgreementArtifactsData.organization.currentCreditAgreementArtifacts
  ) {
    return (
      <Box mt="30px">
        <Table>
          <CreditAgreementsOverviewTableHeader />
          <Tbody>
            <CreditAgreementsOverviewTableSkeletonRow width="100px" />
            <CreditAgreementsOverviewTableSkeletonRow width="75px" />
          </Tbody>
        </Table>
      </Box>
    );
  }

  const {
    organization: { currentCreditAgreementArtifacts },
  } = creditAgreementArtifactsData;

  // @jendevelops TODO: rename methods to artifact, since we're dealing with an artifact (not full artifact) here. This might require a refactor for the wip components that are used in the credit agreement detail page
  let parsedCreditAgreementFullArtifacts = null;

  try {
    parsedCreditAgreementFullArtifacts = _.map(
      currentCreditAgreementArtifacts,
      (creditAgreementFullArtifact) => {
        const { creditAgreement, id } = creditAgreementFullArtifact;
        const parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
        finleyAssert(
          parsedCreditAgreement,
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
        );
        return parsedCreditAgreement;
      },
    );

    const sortedCreditAgreements = _.sortBy(
      parsedCreditAgreementFullArtifacts,
      (creditAgreement) =>
        creditAgreement.facilityMechanics.finalMaturityDate.value,
    );

    return (
      <Box mt="30px">
        <Table>
          <CreditAgreementsOverviewTableHeader />
          <Tbody>
            {_.map(sortedCreditAgreements, (creditAgreement) => {
              return (
                <CreditAgreementOverviewTableRow
                  key={`overview-table-row-${creditAgreement.id}`}
                  creditAgreement={creditAgreement}
                />
              );
            })}
          </Tbody>
        </Table>
      </Box>
    );
  } catch (err) {
    errorToastConfig.title = `Error parsing credit agreement artifacts for organization ${organizationId}`;
    toast(errorToastConfig);
    navigate('/');
  }

  throw new Error('unreachable code here');
};
